import { Printer, Calendar, CheckCircle2, ListChecks, Send } from "lucide-react";
import { useState, useEffect } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";

const GOLD = "#c9a961";

const MODULES = [
  { t: "1 · Prendre les présences", d: "Cours réguliers → « Présences » (créer la session, cocher les élèves). Cours d'essai → « Check-in » (le prospect remplit ses coordonnées via la plateforme AVANT le tatami). Kiosque tablette : code DOJO2026. Check-in membre = géolocalisation active ≤ 150 m. Double check-in : l'élève fait le sien, le coach valide." },
  { t: "2 · Suivi des élèves", d: "Carnet de progression (ceinture, techniques, % de maîtrise, vidéos). Notes du coach attendues pour chaque élève. Centre de suivi : absences/retards ET élèves à féliciter. Carnet santé (médical). Contrats/paiements : gérés par Kru." },
  { t: "3 · Monter un cours", d: "Plans de cours KMT (échauffement → technique → rounds → retour au calme). Horaire officiel sur la plateforme. Couverture du curriculum + Reco IA : Planifier une séance → choisir le groupe → sélectionner les techniques → enregistrer (~2 min). D'où l'importance que chaque élève remplisse son carnet." },
  { t: "4 · Évolution des ceintures", d: "Aucune ceinture débloquée automatiquement — le coach valide TOUJOURS. Progression individuelle : pré-test → évaluation → résumé d'évaluation. Passage groupé possible au cas par cas." },
  { t: "5 · Techniques & recommandations", d: "Bibliothèque vidéo (vitesse 0.5×–1.25×), astuces, démos. Entraînements associés (15-30 min) sous chaque technique = « devoir » (surtout pour les membres en ligne/à distance). Le coach voit les vidéos regardées." },
  { t: "6 · Espace Coach & pilotage", d: "Fiche du coach (discipline, ceinture, rôle, objectifs). À compléter : photos + fiche de Sandra. Objectif par coach + suivi mensuel (atteint / en cours / à revoir). L'indicateur d'engagement n'est pas un pointage de présence ni une comparaison." },
];

const DECISIONS = [
  "Rencontre d'équipe OBLIGATOIRE le 11 septembre 2026 à 19 h.",
  "Présences systématiques à chaque cours + check-in obligatoire pour tout cours d'essai.",
  "Notes du coach activées et attendues pour chaque élève.",
  "Passage de ceinture individuel (pré-test → évaluation → résumé), pas de dates collectives imposées.",
  "Frais d'inscription : 35 $ à la 1re inscription, puis 25 $ au renouvellement (gestion de dossier/assurances).",
  "Formations coachs (PNCE, etc.) : dossier confié à Nancy (sinon Sonia).",
  "Séance de tournage dimanche : vidéos techniques + remplir le carnet de Sandra.",
];

const AGENDA_11 = [
  "Rappel des standards : présences, check-in essais, hygiène et respect.",
  "Démonstration en direct de la plateforme, module par module.",
  "Notes du coach : comment les remplir efficacement.",
  "Planifier une séance + Reco IA : atelier pratique (chacun crée sa 1re séance).",
  "Évaluations de ceinture individuelles : pré-test et résumé.",
  "Calendrier annuel des dates importantes (mini-compétition de novembre 2026, passages, événements).",
  "Fiches coachs (photos + coordonnées) + objectifs pédagogiques du trimestre.",
  "Questions / réponses / ajustements.",
];

export default function RencontreCoachsModule() {
  const [sending, setSending] = useState(false);
  const [fil, setFil] = useState([]);

  useEffect(() => {
    api.get("/coach-cockpit/broadcasts").then(({ data }) => setFil(data.broadcasts || [])).catch(() => {});
  }, [sending]);

  const sendToCoaches = async (notify) => {
    setSending(true);
    try {
      const payload = {
        title: "Résumé de rencontre — Équipe de coachs",
        meeting_date: "28 août 2026",
        intro: "Devenir la meilleure version de nous-mêmes — comme praticiens et comme coachs. On bâtit un système commun, clair et outillé, pour que chaque coach travaille de la même façon, même en l'absence de Kru.",
        next_meeting: "Vendredi 11 septembre 2026 · 19 h 00 — au dojo. Présence obligatoire pour tous les coachs.",
        sections: [
          { heading: "Tour de la plateforme — module par module", items: MODULES.map((m) => `${m.t} — ${m.d}`) },
          { heading: "Décisions prises", items: DECISIONS },
          { heading: "Ordre du jour — 11 septembre", items: AGENDA_11 },
        ],
        notify,
      };
      const { data } = await api.post("/coach-cockpit/broadcast-summary", payload);
      toast.success(`✅ Résumé envoyé au Mini Espace de ${data.coaches} coach(s)${notify ? ` · ${data.notified.length} notifié(s) par SMS` : ""}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Envoi impossible."); }
    setSending(false);
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8" data-testid="rencontre-coachs-page">
      <style>{`@media print { .no-print { display:none !important; } body { background:#fff; } .print-card { break-inside: avoid; } }`}</style>

      <div className="flex items-center justify-between gap-4 mb-6 no-print">
        <div>
          <div className="text-xs uppercase tracking-widest" style={{ color: GOLD }}>Guide Coach · Kaizen System</div>
          <h1 className="text-2xl font-bold">Résumé de rencontre — Équipe de coachs</h1>
        </div>
        <div className="flex flex-wrap gap-2 justify-end">
          <button onClick={() => sendToCoaches(false)} disabled={sending} data-testid="send-coaches-btn"
            className="inline-flex items-center gap-2 bg-emerald-600 text-white font-bold px-4 py-2.5 rounded hover:bg-emerald-500 transition-colors disabled:opacity-50">
            <Send size={18} /> Envoyer au Mini Espace Coach
          </button>
          <button onClick={() => sendToCoaches(true)} disabled={sending} data-testid="send-coaches-notify-btn"
            className="inline-flex items-center gap-2 bg-sky-600 text-white font-bold px-4 py-2.5 rounded hover:bg-sky-500 transition-colors disabled:opacity-50">
            <Send size={18} /> Envoyer + notifier (SMS)
          </button>
          <button onClick={() => window.print()} data-testid="print-btn"
            className="inline-flex items-center gap-2 bg-[#c9a961] text-black font-bold px-4 py-2.5 rounded hover:bg-white transition-colors">
            <Printer size={18} /> Imprimer / PDF
          </button>
        </div>
      </div>

      {fil.length > 0 && (
        <div className="mb-6 rounded-xl border border-zinc-700 bg-zinc-900/40 p-4 no-print" data-testid="fil-rencontres">
          <h2 className="text-sm font-bold text-white mb-2 flex items-center gap-2">📋 Fil des rencontres — accusés de lecture</h2>
          <div className="space-y-2">
            {fil.map((b) => (
              <div key={b.id} className="rounded-lg bg-black/30 border border-white/10 p-3" data-testid={`fil-${b.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm text-white">{b.title} <span className="text-zinc-500 text-xs">· {b.meeting_date}</span></div>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${b.read_count >= b.total_coaches ? "bg-emerald-600/30 text-emerald-200" : "bg-amber-600/30 text-amber-200"}`}>Lu {b.read_count}/{b.total_coaches}</span>
                </div>
                {(b.unread || []).length > 0 && (
                  <div className="text-[11px] text-zinc-500 mt-1">Pas encore lu : {b.unread.join(", ")}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white text-[#0a0a0a] rounded-lg p-8 shadow-xl">
        <header className="border-b-2 pb-4 mb-6" style={{ borderColor: GOLD }}>
          <h2 className="text-xl font-black uppercase">Institut Impact Élite Martial — Rencontre d'équipe</h2>
          <p className="text-sm text-zinc-600 mt-1">28 août 2026 · Animée par Kru Ralph David Jean-Pierre</p>
          <p className="text-sm text-zinc-600">Présents : Coach Sonia Laframboise · Coach Sandra Lessard · Sensei Kru Ralph</p>
        </header>

        <section className="mb-6 print-card">
          <h3 className="font-bold text-lg mb-2">🎯 Intention</h3>
          <p className="text-sm text-zinc-700 leading-relaxed">Devenir la meilleure version de nous-mêmes — comme praticiens et comme coachs. On bâtit un système commun, clair et outillé, pour que chaque coach travaille de la même façon, même en l'absence de Kru.</p>
        </section>

        <section className="mb-6 print-card rounded-md p-4" style={{ background: "#fdf6e3", border: `1px solid ${GOLD}` }}>
          <h3 className="font-bold text-lg flex items-center gap-2"><Calendar size={18} /> Prochaine rencontre OBLIGATOIRE</h3>
          <p className="text-sm mt-1"><b>Vendredi 11 septembre 2026 · 19 h 00</b> — au dojo. Présence obligatoire pour tous les coachs.</p>
        </section>

        <section className="mb-6 print-card">
          <h3 className="font-bold text-lg mb-3">🧭 Tour de la plateforme — module par module</h3>
          <div className="space-y-3">
            {MODULES.map((m) => (
              <div key={m.t} className="border-l-4 pl-3" style={{ borderColor: GOLD }}>
                <div className="font-semibold text-sm">{m.t}</div>
                <p className="text-[13px] text-zinc-700 leading-relaxed">{m.d}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-6 print-card">
          <h3 className="font-bold text-lg mb-2 flex items-center gap-2"><CheckCircle2 size={18} /> Décisions prises</h3>
          <ol className="list-decimal pl-5 space-y-1 text-[13px] text-zinc-700">
            {DECISIONS.map((d, i) => <li key={i}>{d}</li>)}
          </ol>
        </section>

        <section className="print-card">
          <h3 className="font-bold text-lg mb-2 flex items-center gap-2"><ListChecks size={18} /> Ordre du jour — 11 septembre</h3>
          <ol className="list-decimal pl-5 space-y-1 text-[13px] text-zinc-700">
            {AGENDA_11.map((a, i) => <li key={i}>{a}</li>)}
          </ol>
        </section>

        <footer className="mt-8 pt-4 border-t text-[11px] text-zinc-500">
          Document à transmettre à l'équipe de coachs · Institut Impact Élite Martial · Kaizen System · (819) 527-9446
        </footer>
      </div>
    </div>
  );
}
